/**
 * Date Utility Functions
 * 
 * Provides helper functions for date formatting and manipulation.
 */

/**
 * Format a Firestore timestamp to a readable date string
 * @param {Object|Date|string} timestamp - Firestore timestamp, Date object, or date string
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
export function formatDate(timestamp, options = {}) {
  if (!timestamp) return '';
  
  let date;
  
  // Handle Firestore timestamp
  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp.toDate) {
    // Firestore Timestamp object
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    return '';
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString('en-US', defaultOptions);
}

/**
 * Format a Firestore timestamp to a readable date and time string
 * @param {Object|Date|string} timestamp - Firestore timestamp, Date object, or date string
 * @returns {string} - Formatted date and time string
 */
export function formatDateTime(timestamp) {
  if (!timestamp) return '';
  
  let date;
  
  // Handle Firestore timestamp
  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    return '';
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format a Firestore timestamp to a relative time string (e.g., "2 hours ago")
 * @param {Object|Date|string} timestamp - Firestore timestamp, Date object, or date string
 * @returns {string} - Relative time string
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  
  let date;
  
  // Handle Firestore timestamp
  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    return '';
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    return formatDate(timestamp);
  }
}

/**
 * Check if a date is in the past
 * @param {Object|Date|string} timestamp - Firestore timestamp, Date object, or date string
 * @returns {boolean} - True if date is in the past
 */
export function isPast(timestamp) {
  if (!timestamp) return false;
  
  let date;
  
  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    return false;
  }
  
  if (isNaN(date.getTime())) return false;
  
  return date < new Date();
}

/**
 * Check if a date is in the future
 * @param {Object|Date|string} timestamp - Firestore timestamp, Date object, or date string
 * @returns {boolean} - True if date is in the future
 */
export function isFuture(timestamp) {
  if (!timestamp) return false;
  
  let date;
  
  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    return false;
  }
  
  if (isNaN(date.getTime())) return false;
  
  return date > new Date();
}

